import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useUrlState from '../../hooks/useUrlState';
import { useAuth } from '../../auth/AuthProvider';
import Header from '../../components/Header';
import ErrorBanner from '../../components/ErrorBanner';
import { styles, colors, radius, shadows } from '../../styles/theme';
import { useCachedQuery } from '../../hooks/useCachedQuery';
import { searchMatch } from '../../utils/searchMatch';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';
const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const BIRIM_LABELS = { KG: 'kg', ADET: 'adet', LT: 'lt', PAKET: 'paket', KOLI: 'koli', PORSIYON: 'porsiyon' };
const getBirimLabel = (birim) => BIRIM_LABELS[birim] || (birim || 'kg').toLowerCase();

const ABC_COLORS = {
  A: { bg: '#ffebee', color: '#c62828', border: '#ef9a9a' },
  B: { bg: '#fff3e0', color: '#e65100', border: '#ffcc80' },
  C: { bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7' },
};

const StokDurumuPage = () => {
  useAuth();
  const navigate = useNavigate();

  const fetchStok = useCallback(() =>
    fetch(`${API_URL}/api/stok`, { headers: getAuthHeaders() })
      .then(async res => {
        if (res.status === 401) { window.location.href = '/login'; return []; }
        if (!res.ok) {
          // 500'ü yutma: eskiden {"detail":...} Array sayılmayıp [] dönüyor,
          // sayfa "Stok kaydı bulunamadı" gösteriyordu (oysa endpoint çöküyor).
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || `Sunucu hatası (${res.status})`);
        }
        return res.json();
      })
      .then(data => Array.isArray(data) ? data : []),
  []);

  const { data: stoklar = [], isLoading: loading, error: stokError, revalidate } = useCachedQuery(
    'stok-durumu', fetchStok, { staleTime: 10000, revalidateOnFocus: true }
  );
  // Filtreler URL'e bağlı: başka sayfaya gidip geri dönünce (tarayıcı-geri) korunur.
  const [lokasyonFilter, setLokasyonFilter] = useUrlState('lokasyon', '');
  const [aramaQuery, setAramaQuery] = useUrlState('arama', '', { debounceMs: 300 });
  const [abcFilter, setAbcFilter] = useUrlState('abc', '');
  const [abcLoading, setAbcLoading] = useState(false);
  const [aktifLokasyonlar, setAktifLokasyonlar] = useState([]);
  // Eksik üretim logu: POS satışı olup üretim/ikmal girişi olmayan yarı mamuller
  // (lokasyon:malzeme → kayıt). Rozet verisi — hata sessiz (sayfanın ana verisi değil).
  const [eksikUretim, setEksikUretim] = useState({});
  // Pencere (gün) API'den gelir → rozet metni "Son N günde üretim yok". 14 yalnız
  // API gelene kadar fallback (Anayasa M.1: gerçek değer backend'den, varsayılan eşitler).
  const [eksikSonGun, setEksikSonGun] = useState(14);
  // SKT/Bozulma riski rozeti — hafif: yalnız özet okunur (per-satır join YOK).
  // 403 (analitik izni yok) sessizce yutulur → rozet hiç gösterilmez.
  const [sktOzet, setSktOzet] = useState(null);

  // Lokasyon dropdown'ı /api/lokasyonlar'dan beslenir (TRANSIT default'ta hariç)
  useEffect(() => {
    fetch(`${API_URL}/api/lokasyonlar`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(data => setAktifLokasyonlar(Array.isArray(data) ? data : []))
      .catch(() => setAktifLokasyonlar([]));
  }, []);

  useEffect(() => {
    fetch(`${API_URL}/api/stok/eksik-uretim-logu`, { headers: getAuthHeaders() })
      .then(res => (res.ok ? res.json() : { kayitlar: [] }))
      .then(data => {
        const map = {};
        (data.kayitlar || []).forEach(k => { map[`${k.lokasyon_id}:${k.malzeme_id}`] = k; });
        setEksikUretim(map);
        if (data.son_gun) setEksikSonGun(data.son_gun);
      })
      .catch(() => setEksikUretim({}));
  }, []);

  useEffect(() => {
    fetch(`${API_URL}/api/raporlar/skt-bozulma`, { headers: getAuthHeaders() })
      .then(res => (res.ok ? res.json() : null)) // 403/diğer → null (rozet gizli)
      .then(data => setSktOzet(data?.ozet || null))
      .catch(() => setSktOzet(null));
  }, []);

  const sktRiskAdet = (sktOzet?.kirmizi_adet || 0) + (sktOzet?.gecti_adet || 0);

  // Filtreleme
  const filteredStoklar = stoklar.filter(stok => {
    if (lokasyonFilter && stok.lokasyon_ad !== lokasyonFilter) return false;
    if (aramaQuery && !searchMatch(stok.malzeme_ad, aramaQuery)) return false;
    if (abcFilter && stok.abc_sinifi !== abcFilter) return false;
    return true;
  });

  // Lokasyonlara gore grupla
  const gruplanmisStok = filteredStoklar.reduce((acc, stok) => {
    const depo = stok.lokasyon_ad || 'Bilinmeyen Depo';
    if (!acc[depo]) acc[depo] = [];
    acc[depo].push(stok);
    return acc;
  }, {});

  // Lokasyon dropdown listesi — /api/lokasyonlar'dan (TRANSIT default'ta hariç)
  const lokasyonlar = aktifLokasyonlar.map(l => l.ad).sort();

  // Özet sayilar
  const kritikSayisi = filteredStoklar.filter(s => s.durum === 'KRITIK').length;
  const toplamKalem = filteredStoklar.length;
  const eksikUretimSayisi = filteredStoklar.filter(s => eksikUretim[`${s.lokasyon_id}:${s.malzeme_id}`]).length;

  return (
    <div>
      <Header title="Stok Durumu" />
      <div style={{ padding: '20px', background: colors.background, minHeight: 'calc(100vh - 60px)' }}>

        <ErrorBanner error={stokError && (stokError.message || 'Sunucu hatası')} onRetry={revalidate} />

        {/* SKT/Bozulma riski rozeti — kırmızı+geçmiş kalem varsa rapora yönlendirir.
            Analitik izni olmayan kullanıcıda fetch 403 → sktOzet null → rozet gizli. */}
        {sktRiskAdet > 0 && (
          <div
            onClick={() => navigate('/skt-bozulma')}
            title="SKT/Bozulma raporunu aç"
            style={{
              background: '#fff5f5', border: '1px solid #ffcdd2', borderRadius: radius.md,
              padding: '10px 16px', marginBottom: '16px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', color: '#c62828',
            }}
          >
            <span style={{ fontSize: '18px' }}>⚠️</span>
            <span style={{ flex: 1, fontWeight: '600' }}>
              {sktRiskAdet} kalem SKT riski (kırmızı / geçmiş) — SKT / Bozulma raporu
            </span>
            <span style={{ fontSize: '13px', textDecoration: 'underline' }}>Aç →</span>
          </div>
        )}

        {/* Özet Kartlar */}
        {!loading && (
          <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap' }}>
            <div style={{
              background: colors.surface, borderRadius: radius.md, padding: '16px 24px',
              boxShadow: shadows.sm, minWidth: '150px'
            }}>
              <div style={{ fontSize: '12px', color: colors.textSecondary, marginBottom: '4px' }}>Toplam Kalem</div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: styles.primary }}>{toplamKalem}</div>
            </div>
            {kritikSayisi > 0 && (
              <div style={{
                background: '#fff5f5', borderRadius: radius.md, padding: '16px 24px',
                boxShadow: shadows.sm, border: '1px solid #ffcdd2', minWidth: '150px'
              }}>
                <div style={{ fontSize: '12px', color: '#c62828', marginBottom: '4px' }}>Kritik Stok</div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#c62828' }}>{kritikSayisi}</div>
              </div>
            )}
            {eksikUretimSayisi > 0 && (
              <div
                title="POS satışı var ama son günlerde üretim/ikmal girişi yok — yarı mamul stoğu eksiye gider, hammadde tüketimi kaydedilmez. Üretim kayıtlarını girin."
                style={{
                  background: '#fff3e0', borderRadius: radius.md, padding: '16px 24px',
                  boxShadow: shadows.sm, border: '1px solid #ffcc80', minWidth: '150px'
                }}
              >
                <div style={{ fontSize: '12px', color: '#e65100', marginBottom: '4px' }}>Üretim Girişi Eksik</div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#e65100' }}>{eksikUretimSayisi}</div>
              </div>
            )}
            <div style={{
              background: colors.surface, borderRadius: radius.md, padding: '16px 24px',
              boxShadow: shadows.sm, minWidth: '150px'
            }}>
              <div style={{ fontSize: '12px', color: colors.textSecondary, marginBottom: '4px' }}>Lokasyon Sayısı</div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: styles.secondary }}>{lokasyonlar.length}</div>
            </div>
            {filteredStoklar.filter(s => s.abc_sinifi === 'A').length > 0 && (
              <div style={{
                background: '#fff5f5', borderRadius: radius.md, padding: '16px 24px',
                boxShadow: shadows.sm, border: '1px solid #ef9a9a', minWidth: '150px'
              }}>
                <div style={{ fontSize: '12px', color: '#c62828', marginBottom: '4px' }}>A Sınıfı</div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#c62828' }}>
                  {filteredStoklar.filter(s => s.abc_sinifi === 'A').length}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Filtre Bari */}
        {!loading && stoklar.length > 0 && (
          <div style={{
            background: colors.surface, borderRadius: radius.md, padding: '12px 16px',
            marginBottom: '20px', boxShadow: shadows.sm,
            display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap'
          }}>
            <input
              type="text"
              value={aramaQuery}
              onChange={e => setAramaQuery(e.target.value)}
              placeholder="Malzeme ara..."
              style={{
                padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm,
                fontSize: '14px', minWidth: '200px', boxSizing: 'border-box'
              }}
            />
            <select
              value={lokasyonFilter}
              onChange={e => setLokasyonFilter(e.target.value)}
              style={{ padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '14px' }}
            >
              <option value="">Tüm Lokasyonlar</option>
              {lokasyonlar.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
            <select
              value={abcFilter}
              onChange={e => setAbcFilter(e.target.value)}
              style={{ padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '14px' }}
            >
              <option value="">Tüm ABC</option>
              <option value="A">A Sınıfı</option>
              <option value="B">B Sınıfı</option>
              <option value="C">C Sınıfı</option>
            </select>
            {(aramaQuery || lokasyonFilter || abcFilter) && (
              <button
                onClick={() => { setAramaQuery(''); setLokasyonFilter(''); setAbcFilter(''); }}
                style={{
                  background: 'none', border: 'none', color: styles.primary,
                  cursor: 'pointer', fontSize: '13px', textDecoration: 'underline'
                }}
              >Temizle</button>
            )}
            <button
              onClick={() => {
                const rows = [['Lokasyon', 'Malzeme', 'Miktar', 'Birim', 'Min Stok', 'Durum', 'ABC Sınıfı']];
                filteredStoklar.forEach(s => {
                  rows.push([s.lokasyon_ad || '', s.malzeme_ad || '', s.miktar, getBirimLabel(s.birim), s.min_stok || '', s.durum || '', s.abc_sinifi || '']);
                });
                const csv = rows.map(r => r.join(';')).join('\n');
                const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a'); a.href = url;
                a.download = `stok-durumu-${new Date().toISOString().split('T')[0]}.csv`;
                a.click(); URL.revokeObjectURL(url);
              }}
              style={{ padding: '8px 14px', background: '#1976d2', color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}
            >CSV Indir</button>
            <button
              onClick={async () => {
                setAbcLoading(true);
                try {
                  const token = localStorage.getItem('access_token');
                  const res = await fetch(`${API_URL}/api/abc/hesapla`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` }
                  });
                  if (res.ok) {
                    const data = await res.json();
                    alert(`ABC analizi tamamlandı. ${data.guncellenen} malzeme güncellendi.`);
                    window.location.reload();
                  } else {
                    const err = await res.json().catch(() => ({}));
                    alert(err.detail || 'ABC hesaplama hatası');
                  }
                } catch (err) {
                  logError('ABC hesaplama hatası:', err);
                }
                setAbcLoading(false);
              }}
              disabled={abcLoading}
              style={{ padding: '8px 14px', background: '#7b1fa2', color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontSize: '13px', fontWeight: '600', opacity: abcLoading ? 0.6 : 1 }}
            >{abcLoading ? 'Hesaplanıyor...' : 'ABC Hesapla'}</button>
            <span style={{ marginLeft: 'auto', fontSize: '13px', color: colors.textSecondary }}>
              {filteredStoklar.length} kalem gosteriliyor
            </span>
          </div>
        )}

        {/* Stok Kartlari */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: colors.textSecondary }}>Yükleniyor...</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '20px' }}>
            {Object.keys(gruplanmisStok).length === 0 ? (
              <div style={{ gridColumn: '1/-1', textAlign: 'center', color: colors.textSecondary, padding: '40px' }}>
                {stoklar.length === 0 ? 'Stok kaydi bulunamadı.' : 'Filtreye uygun sonuc yok.'}
              </div>
            ) : Object.entries(gruplanmisStok).map(([depo, urunler]) => {
              const depoKritik = urunler.filter(u => u.durum === 'KRITIK').length;
              return (
                <div key={depo} style={{
                  background: colors.surface, padding: '20px', borderRadius: radius.md,
                  boxShadow: shadows.sm,
                  borderTop: depoKritik > 0 ? '3px solid #c62828' : '3px solid #4caf50'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', borderBottom: `1px solid ${colors.border}`, paddingBottom: '10px' }}>
                    <h3 style={{ margin: 0, fontSize: '16px' }}>{depo}</h3>
                    <span style={{ fontSize: '12px', color: colors.textTertiary }}>{urunler.length} kalem</span>
                  </div>
                  {urunler.map(stok => {
                    const eksikKayit = eksikUretim[`${stok.lokasyon_id}:${stok.malzeme_id}`];
                    return (
                    <div key={stok.id} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px dashed #f0f0f0'
                    }}>
                      <span style={{ fontSize: '14px' }}>
                        {stok.abc_sinifi && (
                          <span style={{
                            display: 'inline-block', padding: '1px 6px', borderRadius: radius.sm,
                            fontSize: '10px', fontWeight: 'bold', marginRight: '6px',
                            background: (ABC_COLORS[stok.abc_sinifi] || {}).bg || '#f5f5f5',
                            color: (ABC_COLORS[stok.abc_sinifi] || {}).color || '#333',
                            border: `1px solid ${(ABC_COLORS[stok.abc_sinifi] || {}).border || '#ddd'}`,
                          }}>
                            {stok.abc_sinifi}
                          </span>
                        )}
                        {stok.malzeme_ad}
                        {eksikKayit && (
                          <span
                            title={`POS satışı ${Number(eksikKayit.satis_miktar ?? 0).toFixed(1)} ${getBirimLabel(eksikKayit.birim)} ama son ${eksikSonGun} günde üretim/ikmal girişi yok. ${eksikKayit.son_ikmal_tarihi ? `Son üretim/giriş: ${new Date(eksikKayit.son_ikmal_tarihi).toLocaleDateString('tr-TR')}.` : 'Hiç üretim/giriş kaydı yok.'} Üretim kaydını girin.`}
                            style={{
                              display: 'inline-block', padding: '1px 6px', borderRadius: radius.sm,
                              fontSize: '10px', fontWeight: 'bold', marginLeft: '6px',
                              background: '#fff3e0', color: '#e65100', border: '1px solid #ffcc80',
                            }}
                          >
                            Son {eksikSonGun} günde üretim yok
                          </span>
                        )}
                      </span>
                      <div style={{ textAlign: 'right' }}>
                        <strong style={{
                          color: stok.durum === 'KRITIK' ? '#c62828' : '#333',
                          fontSize: '14px'
                        }}>
                          {Number(stok.miktar ?? 0).toFixed(2)} {getBirimLabel(stok.birim)}
                        </strong>
                        {stok.durum === 'KRITIK' && (
                          <div style={{ fontSize: '11px', color: '#c62828', fontWeight: 'bold' }}>
                            Min: {Number(stok.min_stok ?? 0).toFixed(2)} {getBirimLabel(stok.birim)}
                          </div>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default StokDurumuPage;
